import { useAtomValue } from "jotai";
import isString from "lodash/isString";
import type { NextPageContext, PageProps } from "next";
import { memo } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { config as configData } from "shared/data";

import { useTopBannerAdSlot } from "scmp-app/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import type { AppBarVariant } from "scmp-app/components/app-bar/types";
import type { Application } from "scmp-app/components/app-initializer/helpers";
import { ArticleLinkingData } from "scmp-app/components/article/article-linking-data";
import { ArticleList } from "scmp-app/components/article/article-list";
import { DefaultCustomContents } from "scmp-app/components/article/article-render/consts";
import { useArticleBaseAdvertisingInfo } from "scmp-app/components/article/article-render/hooks";
import { PlusCustomContents } from "scmp-app/components/article/article-render/plus/consts";
import { StyleCustomContents } from "scmp-app/components/article/article-render/style-article/consts";
import { ArticleRenderType } from "scmp-app/components/article/article-render/types";
import { ArticleSchema } from "scmp-app/components/article/article-schema";
import { ArticleSeo } from "scmp-app/components/article/article-seo";
import { ContentShareWidgetDrawer } from "scmp-app/components/content/content-share-widget-drawer";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { PageSeo } from "scmp-app/components/meta";
import { currentArticleAtom } from "scmp-app/lib/current-item";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type {
  articlePageQuery,
  BodyFormatTypeEnum,
} from "scmp-app/queries/__generated__/articlePageQuery.graphql";

import { decryptedShare } from "./helpers";
import {
  useAmpRedirect,
  useArticleAcknowledgementGateOverlay,
  useArticlePageAdvertisementConfig,
  useTrackContentInterest,
  useTrackPageView,
} from "./hooks";
import { Article } from "./styles";

export const query = graphql`
  query articlePageQuery(
    $appConfigEntityId: String!
    $bodyType: BodyFormatTypeEnum
    $customContents: [CustomContentInput]
    $entityId: String!
    $entityUuid: String!
    $isVideo: Boolean!
  ) {
    article(filter: { entityId: $entityId }) @skip(if: $isVideo) {
      ...articleBaseAdvertisingInfoArticle
      ...articleListContent @arguments(customContents: $customContents, bodyType: $bodyType)
      ...articleSeoArticle @arguments(customContents: $customContents, bodyType: $bodyType)
      ...hooksArticleAcknowledgementGateOverlayArticle
      ...hooksTrackPageViewArticle
      ...hooksContentInterestArticle
      ...articleLinkingDataContent
      ...hooksAmpRedirectArticle
      entityId
    }
    videoContent: content(contentType: VIDEO, filter: { entityId: $entityId })
      @include(if: $isVideo) {
      headline
      ...articleBaseAdvertisingInfoArticle
      ...articleListContent @arguments(customContents: $customContents, bodyType: $bodyType)
      ...hooksArticleAcknowledgementGateOverlayArticle
      ...hooksTrackPageViewArticle
      ...hooksContentInterestArticle
      ...articleLinkingDataContent
      entityId
    }
    ...articleSchemaContent
      @arguments(entityId: $entityId, entityUuid: $entityUuid, bodyType: $bodyType)
      @skip(if: $isVideo)
    ...hooksArticlePageAdvertisementConfigArticle @arguments(appConfigEntityId: $appConfigEntityId)
  }
`;

const ArticlePage: RelayComponent<PageProps, articlePageQuery> = ({
  appBarConfiguration,
  preloadedQuery,
  shareContentUnlocked,
}) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const content = data.article ?? data.videoContent;
  const isVideo = !data.article && !!data.videoContent;

  const advertisingInfo = useArticleBaseAdvertisingInfo(content!);
  const currentArticle = useAtomValue(currentArticleAtom);
  const { isAcknowledgementRequired, renderAcknowledgementGateOverlay } =
    useArticleAcknowledgementGateOverlay(content!);

  useAmpRedirect(data.article ?? null);
  useTrackPageView(currentArticle ?? content!, shareContentUnlocked);
  useTrackContentInterest(currentArticle ?? content!);
  useTopBannerAdSlot({
    desktop: null,
    mobile: appBarConfiguration?.hasMobileAd
      ? {
          ...advertisingInfo,
          adUnit: "m_banner3",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: content?.entityId ?? "",
            },
          },
        }
      : null,
  });
  useArticlePageAdvertisementConfig({
    reference: data,
  });

  if (!content) return null;

  return (
    <>
      {isVideo ? (
        <PageSeo title={data.videoContent?.headline ?? undefined} />
      ) : (
        <ArticleSeo reference={currentArticle ?? data.article!} />
      )}
      <ContentShareWidgetDrawer />
      {!isVideo && <ArticleLinkingData reference={data.article!} />}
      {!isVideo && <ArticleSchema reference={data} />}
      {renderAcknowledgementGateOverlay()}
      {/* TODO: workaround for fixing https://scmp-product-tech.atlassian.net/browse/SCMPWEB-3430, third party should not relied on the application level */}
      <Article $isShowArticle={!isAcknowledgementRequired} className="article-list">
        <List reference={content} shareContentUnlocked={shareContentUnlocked} />
      </Article>
    </>
  );
};

const List = memo(ArticleList);

ArticlePage.displayName = "ArticlePage";

const getPageProps = (context: NextPageContext) => {
  const appBarVariant = context.query.appBarVariant as AppBarVariant;
  const application = context.query.application as Application;
  const renderType = context.query.renderType as ArticleRenderType | undefined;

  const shareContentUnlocked =
    isString(context.query.entityId) && isString(context.query?.share)
      ? decryptedShare(context)
      : undefined;

  const pageProps: PageProps = {
    animateKey: `article-${context.query.entityId?.toString() ?? ""}`,
    appBarConfiguration: {
      hasMobileAd: appBarVariant === "scmp/generic-light-with-banner-ad",
      variant: appBarVariant,
    },
    appInitializerConfiguration: {
      application,
    },
    contentConfiguration: {
      // check for article is generic and use the new padding config
      responsivePadding: (() => {
        switch (renderType) {
          case ArticleRenderType.Generic:
          case ArticleRenderType.Video:
            return {
              desktopUp: 12,
              mobileUp: 4,
              tabletUp: 12,
            };
          default:
            return {
              desktopUp: 32,
              mediumDesktopUp: 52,
              tabletUp: 12,
            };
        }
      })(),
    },
    shareContentUnlocked,
  };

  if (appBarVariant === "scmp/magazines-style") {
    pageProps.pageConfiguration = {
      ...pageProps.pageConfiguration,
      responsivePadding: {
        desktopUp: 40,
        mobileUp: 16,
        tabletUp: 20,
      },
    };
    pageProps.contentConfiguration = {
      ...pageProps.contentConfiguration,
      responsivePadding: {
        desktopUp: 135,
        mobileUp: 24,
        tabletUp: 70,
      },
    };
  }

  return pageProps;
};

export default withRelay(ArticlePage, query, {
  clientSideProps: context => getPageProps(context),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: context => Promise.resolve(getPageProps(context)),
  variablesFromContext(context) {
    const appBarVariant = context.query.appBarVariant as AppBarVariant;
    const isVideo = context.query.isVideo === "true";

    const getCustomContents = () => {
      switch (appBarVariant) {
        case "scmp/magazines-style":
          return StyleCustomContents;
        case "scmp/plus":
          return PlusCustomContents;
        default:
          return DefaultCustomContents;
      }
    };

    const bodyType: BodyFormatTypeEnum =
      appBarVariant === "scmp/magazines-style" ? "PARAGRAPHANDMEDIA" : "PARAGRAPH";

    return isString(context.query.entityId) && (isString(context.query.entityUuid) || isVideo)
      ? {
          appConfigEntityId: configData.advertisement.configId,
          bodyType,
          customContents: getCustomContents(),
          entityId: context.query.entityId,
          entityUuid: isString(context.query.entityUuid) ? context.query.entityUuid : "",
          isVideo,
        }
      : {
          appConfigEntityId: configData.advertisement.configId,
          bodyType,
          customContents: [],
          entityId: "",
          entityUuid: "",
          isVideo: false,
        };
  },
});
